import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { uploadListingPhoto } from '../../lib/storageService.js';
import {
  checkCanScan,
  consumeScan,
  analyzeImage,
  verifyIsDiskCover,
  iaScanErrorMessage,
  aiScanQualifiesForBilling,
  iaScanNoBillingMessage,
  countAiScanFilledFields,
} from '../../lib/aiScansService.js';
import { compressImageForAiScan } from '../../lib/storageService.js';
import { AI_SCAN_COST, GROOVES_PHONE_GATE_USER_MESSAGE } from '../constants/groovesEconomy.js';
import { Colors as C } from '../../constants/Colors.js';
import Icon from './Icon.js';
import { DCard, BtnGold, BtnGhost, FieldInput, Toggle } from './MarketplaceAtoms.js';
import { GENEROS_TREE } from '../../constants/musicConstants.js';
import {
  applyPublicarFieldsFromAi,
  inpBase,
  TipoPublicacionRow,
  YesNoRow,
  EstadoBloque,
  FORMATO_OPTS,
  GENEROS_PRINCIPALES,
} from '../publicarFormShared.js';
import { insertProfileColeccionItem } from '../../lib/profileColeccion.js';

function promiseWithTimeout(promise, ms, timeoutMessage) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(timeoutMessage)), ms);
    promise
      .then(res => {
        clearTimeout(t);
        resolve(res);
      })
      .catch(err => {
        clearTimeout(t);
        reject(err);
      });
  });
}

const INSERT_MS = 26000;

/**
 * Modal «Añadir a colección» con la misma mecánica que Publicar (manual / IA, fotos, tipo, edición especial, campos de disco),
 * sin precio, envío ni ubicación de venta.
 */
export default function ColeccionItemFormModal({ open, onClose, user, onSaved, onNavigate }) {
  const [modo, setModo] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [done, setDone] = useState(false);

  const [esConjunto, setEsConjunto] = useState(false);
  const [artista, setArtista] = useState('');
  const [titulo, setTitulo] = useState('');
  const [anio, setAnio] = useState('');

  const [esEdicionEspecial, setEsEdicionEspecial] = useState(false);
  const [edicionNombre, setEdicionNombre] = useState('');
  const [edicionCatalogo, setEdicionCatalogo] = useState('');
  const [edicionContenido, setEdicionContenido] = useState('');
  const [edicionNumerada, setEdicionNumerada] = useState(false);
  const [edicionNumeroEjemplar, setEdicionNumeroEjemplar] = useState('');

  const [conjuntoTitulo, setConjuntoTitulo] = useState('');
  const [conjuntoNumDiscos, setConjuntoNumDiscos] = useState(2);
  const [conjuntoDescripcion, setConjuntoDescripcion] = useState('');

  const [estado, setEstado] = useState('');
  const [sello, setSello] = useState('');
  const [pais, setPais] = useState('');
  const [formato, setFormato] = useState('Vinilo 12"');
  const [genero, setGenero] = useState('');
  const [generoSub, setGeneroSub] = useState('');
  const [obs, setObs] = useState('');
  const [notasExtra, setNotasExtra] = useState('');

  const [isJoya, setIsJoya] = useState(false);
  const [openToOffers, setOpenToOffers] = useState(false);

  const [groovesDisponibles, setGroovesDisponibles] = useState(null);
  const [iaGateReason, setIaGateReason] = useState(null);
  const [iaAnalysisError, setIaAnalysisError] = useState(null);
  const [iaScanNote, setIaScanNote] = useState(null);
  const [puedeUsarIA, setPuedeUsarIA] = useState(false);
  const [fotosFiles, setFotosFiles] = useState([null, null, null]);
  const [fotosPreviews, setFotosPreviews] = useState([null, null, null]);
  const fotoInputRef0 = useRef(null);
  const fotoInputRef1 = useRef(null);
  const fotoInputRef2 = useRef(null);
  const fotosInputRefs = useMemo(() => [fotoInputRef0, fotoInputRef1, fotoInputRef2], []);
  const [camposInciertos, setCamposInciertos] = useState([]);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const clearFormFields = useCallback(() => {
    setEsConjunto(false);
    setArtista('');
    setTitulo('');
    setAnio('');
    setEsEdicionEspecial(false);
    setEdicionNombre('');
    setEdicionCatalogo('');
    setEdicionContenido('');
    setEdicionNumerada(false);
    setEdicionNumeroEjemplar('');
    setConjuntoTitulo('');
    setConjuntoNumDiscos(2);
    setConjuntoDescripcion('');
    setEstado('');
    setSello('');
    setPais('');
    setFormato('Vinilo 12"');
    setGenero('');
    setGeneroSub('');
    setObs('');
    setNotasExtra('');
    setIsJoya(false);
    setOpenToOffers(false);
    setFotosFiles([null, null, null]);
    setFotosPreviews([null, null, null]);
    setCamposInciertos([]);
    setSubmitting(false);
    setSubmitError(null);
  }, []);

  const resetAll = useCallback(() => {
    clearFormFields();
    setModo(null);
    setAnalyzing(false);
    setDone(false);
  }, [clearFormFields]);

  useEffect(() => {
    if (!open) return;
    resetAll();
  }, [open, resetAll]);

  useEffect(() => {
    if (!open || !user?.id) return;
    checkCanScan(user.id).then(({ canScan, grooves, reason }) => {
      setPuedeUsarIA(canScan);
      setGroovesDisponibles(grooves);
      setIaGateReason(reason || null);
    });
  }, [open, user?.id]);

  const setTipoConjunto = useCallback(next => {
    if (next) {
      setEsConjunto(true);
      setArtista('');
      setTitulo('');
      setAnio('');
      setEsEdicionEspecial(false);
      setEdicionNombre('');
      setEdicionCatalogo('');
      setEdicionContenido('');
      setEdicionNumerada(false);
      setEdicionNumeroEjemplar('');
    } else {
      setEsConjunto(false);
      setConjuntoTitulo('');
      setConjuntoDescripcion('');
      setConjuntoNumDiscos(2);
      setArtista('');
      setTitulo('');
      setAnio('');
    }
  }, []);

  const setEdicionEspecialValue = useCallback(v => {
    setEsEdicionEspecial(v);
    if (!v) {
      setEdicionNombre('');
      setEdicionCatalogo('');
      setEdicionContenido('');
      setEdicionNumerada(false);
      setEdicionNumeroEjemplar('');
    }
  }, []);

  const toBase64File = useCallback(
    f =>
      new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => {
          const s = String(r.result);
          const i = s.indexOf(',');
          res(i >= 0 ? s.slice(i + 1) : s);
        };
        r.onerror = () => rej(new Error('Error leyendo imagen'));
        r.readAsDataURL(f);
      }),
    []
  );

  const runIaAnalysisFromFile = useCallback(
    async file => {
      if (!file) return;
      setAnalyzing(true);
      setDone(false);
      setIaAnalysisError(null);
      setIaScanNote(null);
      setCamposInciertos([]);
      try {
        let fileForIa = file;
        try {
          fileForIa = await compressImageForAiScan(file);
        } catch {
          /* usar original */
        }
        const base64 = await toBase64File(fileForIa);
        const mediaType = fileForIa.type || 'image/jpeg';
        const { fields, lowConfidence, error } = await analyzeImage(base64, mediaType);
        if (error) {
          console.error('[ColeccionModal] analyzeImage:', error);
          setIaAnalysisError(iaScanErrorMessage(error));
          setDone(false);
          return;
        }
        applyPublicarFieldsFromAi(fields, {
          setArtista,
          setTitulo,
          setAnio,
          setSello,
          setPais,
          setFormato,
          setGenero,
          setGeneroSub,
        });
        setCamposInciertos(Array.isArray(lowConfidence) ? lowConfidence : []);
        const qualifiesForBilling = aiScanQualifiesForBilling(fields);
        const filledCount = countAiScanFilledFields(fields);
        if (user?.id && qualifiesForBilling) {
          const { ok, groovesRemaining, error: consumeErr } = await consumeScan(user.id);
          if (ok) {
            setGroovesDisponibles(groovesRemaining);
            setPuedeUsarIA(groovesRemaining >= AI_SCAN_COST);
            setIaGateReason(null);
            setIaScanNote(null);
          } else {
            setGroovesDisponibles(groovesRemaining);
            setPuedeUsarIA(false);
            if (consumeErr === 'phone_not_verified') setIaGateReason('phone_not_verified');
            else
              checkCanScan(user.id).then(({ canScan, grooves, reason }) => {
                setPuedeUsarIA(canScan);
                setGroovesDisponibles(grooves);
                setIaGateReason(reason || null);
              });
          }
        } else if (!qualifiesForBilling) {
          setIaScanNote(iaScanNoBillingMessage(fields));
        }
        setDone(filledCount > 0);
      } catch (err) {
        console.error('[ColeccionModal] IA:', err);
        setIaAnalysisError(iaScanErrorMessage('network_error'));
        setDone(false);
      } finally {
        setAnalyzing(false);
      }
    },
    [user?.id, toBase64File]
  );

  const handleFotoSelect = useCallback(
    async (e, slotIndex) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = '';
      const reader = new FileReader();
      reader.onload = ev => {
        setFotosPreviews(prev => {
          const next = [...prev];
          next[slotIndex] = ev.target.result;
          return next;
        });
      };
      reader.readAsDataURL(file);
      setFotosFiles(prev => {
        const next = [...prev];
        next[slotIndex] = file;
        return next;
      });
      if (slotIndex === 0 && modo === 'ia') {
        await runIaAnalysisFromFile(file);
      }
    },
    [modo, runIaAnalysisFromFile]
  );

  const handleFotoClear = useCallback(
    (slotIndex, e) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      setFotosFiles(prev => {
        const next = [...prev];
        next[slotIndex] = null;
        return next;
      });
      setFotosPreviews(prev => {
        const next = [...prev];
        next[slotIndex] = null;
        return next;
      });
      const input = fotosInputRefs[slotIndex]?.current;
      if (input) input.value = '';
    },
    [fotosInputRefs]
  );

  const fotoSlotsUi = (suffix = '') => (
    <div style={{ marginBottom: 6 }}>
      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>Fotos · máximo 3</div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
        {[
          { label: '★ Portada', sublabel: 'Vinilo · CD · Casete', index: 0 },
          { label: 'Cara del disco', sublabel: 'Etiqueta o cara A/B', index: 1 },
          { label: 'Otros', sublabel: 'Extras', index: 2 },
        ].map(({ label, sublabel, index }) => (
          <div key={`${suffix}-${index}`} style={{ flex: 1, display: 'flex', flexDirection: 'row', alignItems: 'stretch', gap: 6, minWidth: 0 }}>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <input
                ref={fotosInputRefs[index]}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={e => handleFotoSelect(e, index)}
              />
              <div
                role="button"
                tabIndex={0}
                onClick={() => fotosInputRefs[index].current?.click()}
                onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && fotosInputRefs[index].current?.click()}
                style={{
                  width: '100%',
                  aspectRatio: '1',
                  borderRadius: 8,
                  background: fotosPreviews[index] ? 'transparent' : index === 0 ? '#2d1b69' : 'rgba(255,255,255,0.03)',
                  border: fotosPreviews[index]
                    ? `1.5px solid ${index === 0 ? C.gold : 'rgba(255,255,255,0.2)'}`
                    : index === 0
                      ? '1.5px dashed rgba(200,168,75,0.4)'
                      : '1.5px dashed rgba(255,255,255,0.15)',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  minHeight: 70,
                }}
              >
                {fotosPreviews[index] ? (
                  <img src={fotosPreviews[index]} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <span style={{ fontSize: index === 0 ? 22 : 18, color: 'rgba(255,255,255,0.25)' }}>{index === 0 ? '🖼️' : '+'}</span>
                )}
              </div>
              <div style={{ fontSize: 9, color: index === 0 ? C.gold : 'rgba(255,255,255,0.35)', textAlign: 'center', lineHeight: 1.3 }}>{label}</div>
              <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.25)', textAlign: 'center', lineHeight: 1.3 }}>{sublabel}</div>
            </div>
            {fotosPreviews[index] ? (
              <button
                type="button"
                disabled={analyzing}
                onClick={e => handleFotoClear(index, e)}
                title="Quitar esta foto"
                style={{
                  alignSelf: 'center',
                  flexShrink: 0,
                  padding: '6px 8px',
                  fontSize: 10,
                  fontWeight: 600,
                  borderRadius: 6,
                  border: '1px solid rgba(226, 75, 74, 0.45)',
                  background: analyzing ? 'rgba(255,255,255,0.04)' : 'rgba(226, 75, 74, 0.12)',
                  color: analyzing ? 'rgba(255,255,255,0.25)' : '#E89797',
                  cursor: analyzing ? 'not-allowed' : 'pointer',
                }}
              >
                Quitar
              </button>
            ) : null}
          </div>
        ))}
      </div>
      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', textAlign: 'center', marginBottom: 8 }}>
        La portada (★) se verifica con IA al guardar · JPG, PNG, WEBP
      </div>
    </div>
  );

  const handleGuardarColeccion = useCallback(async () => {
    setSubmitError(null);
    if (!user?.id) {
      setSubmitError('Debes iniciar sesión.');
      return;
    }
    if (!estado) {
      setSubmitError('Selecciona el estado del artículo.');
      return;
    }
    if (!genero.trim()) {
      setSubmitError('Selecciona un género principal.');
      return;
    }
    if (esConjunto && !conjuntoTitulo.trim()) {
      setSubmitError('Añade un título al conjunto.');
      return;
    }
    if (!esConjunto && !artista.trim()) {
      setSubmitError('El campo Artista es obligatorio.');
      return;
    }
    if (!esConjunto && !titulo.trim()) {
      setSubmitError('El campo Título es obligatorio.');
      return;
    }
    if (esEdicionEspecial && !edicionNombre.trim()) {
      setSubmitError('Indica el nombre de la edición especial.');
      return;
    }

    let yearVal = null;
    if (anio != null && String(anio).trim() !== '') {
      const y = parseInt(String(anio).trim(), 10);
      if (Number.isFinite(y)) yearVal = y;
    }

    const genreFinal = (generoSub.trim() || genero.trim()) || null;

    setSubmitting(true);
    try {
      let coverUrl = null;
      let url2 = null;
      let url3 = null;

      for (let i = 0; i < 3; i++) {
        const file = fotosFiles[i];
        if (!file) continue;
        if (i === 0) {
          const base64 = await toBase64File(file);
          const { isValid } = await verifyIsDiskCover(base64, file.type || 'image/jpeg');
          if (!isValid) {
            setSubmitError('La imagen de ★ Portada no parece una carátula de álbum. Sustitúyela o quítala.');
            setSubmitting(false);
            return;
          }
          const { url, error: upErr } = await uploadListingPhoto(file, user.id, null);
          if (upErr) {
            setSubmitError('No se pudo subir la portada: ' + upErr);
            setSubmitting(false);
            return;
          }
          if (url) coverUrl = url;
        } else {
          const { url, error: upErr } = await uploadListingPhoto(file, user.id, null);
          if (upErr) {
            setSubmitError(`No se pudo subir la foto ${i + 1}: ` + upErr);
            setSubmitting(false);
            return;
          }
          if (url) {
            if (i === 1) url2 = url;
            if (i === 2) url3 = url;
          }
        }
      }

      const payload = {
        title: esConjunto ? conjuntoTitulo.trim() : titulo.trim(),
        artist: esConjunto ? '(Varios)' : artista.trim(),
        release_year: yearVal,
        format: formato,
        condition_text: notasExtra.trim() || null,
        notes: null,
        genre: genreFinal,
        label_name: sello.trim() || null,
        country: pais.trim() || null,
        condition_code: estado,
        description: obs.trim() || null,
        cover_url: coverUrl,
        photo_url_2: url2,
        photo_url_3: url3,
        es_conjunto: esConjunto,
        conjunto_titulo: esConjunto ? conjuntoTitulo.trim() || null : null,
        conjunto_num_discos: esConjunto ? conjuntoNumDiscos : null,
        conjunto_descripcion: esConjunto ? conjuntoDescripcion.trim() || null : null,
        es_edicion_especial: esEdicionEspecial,
        edicion_nombre: esEdicionEspecial ? edicionNombre.trim() || null : null,
        edicion_catalogo: esEdicionEspecial ? edicionCatalogo.trim() || null : null,
        edicion_contenido: esEdicionEspecial ? edicionContenido.trim() || null : null,
        edicion_numerada: esEdicionEspecial ? edicionNumerada : false,
        edicion_numero_ejemplar: esEdicionEspecial && edicionNumerada ? edicionNumeroEjemplar.trim() || null : null,
        is_joya: isJoya,
        open_to_offers: openToOffers,
      };

      const req = insertProfileColeccionItem(user.id, payload);
      const created = await promiseWithTimeout(req, INSERT_MS, 'La solicitud tardó demasiado. Inténtalo de nuevo.');
      onSaved?.(created);
      onClose?.();
    } catch (err) {
      const msg = err?.message || '';
      setSubmitError(
        msg.includes('does not exist') || msg.includes('relation') || err?.code === '42P01'
          ? 'Falta tabla o columnas en Supabase. Aplica migraciones profile_coleccion (231300, 291200, 291400).'
          : msg || 'No se pudo guardar en la colección.'
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    user,
    estado,
    genero,
    generoSub,
    esConjunto,
    conjuntoTitulo,
    conjuntoNumDiscos,
    conjuntoDescripcion,
    artista,
    titulo,
    anio,
    formato,
    sello,
    pais,
    obs,
    notasExtra,
    esEdicionEspecial,
    edicionNombre,
    edicionCatalogo,
    edicionContenido,
    edicionNumerada,
    edicionNumeroEjemplar,
    fotosFiles,
    isJoya,
    openToOffers,
    toBase64File,
    onSaved,
    onClose,
  ]);

  if (!open) return null;

  return (
    <>
      <div
        onClick={() => !submitting && onClose()}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 400 }}
        role="presentation"
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="coleccion-form-modal-title"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%,-50%)',
          width: 'min(700px,96vw)',
          maxHeight: '92vh',
          overflowY: 'auto',
          background: 'linear-gradient(180deg,#1a1d28,#12141c)',
          borderRadius: 16,
          border: '1px solid rgba(200,168,75,0.28)',
          boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
          padding: '20px 20px 18px',
          zIndex: 401,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, gap: 12 }}>
          <div>
            <h2 id="coleccion-form-modal-title" style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#fff' }}>
              Añadir a tu colección
            </h2>
            <p style={{ margin: '6px 0 0', fontSize: 11, color: 'rgba(255,255,255,0.45)', lineHeight: 1.45 }}>
              Misma mecánica que <strong style={{ color: C.gold }}>Publicar</strong>: manual o IA, fotos y ficha del disco. No se publica a la venta.
            </p>
          </div>
          <button
            type="button"
            disabled={submitting}
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: submitting ? 'default' : 'pointer', padding: 4, color: 'rgba(255,255,255,0.45)' }}
          >
            <Icon name="x" size={20} color="rgba(255,255,255,0.45)" />
          </button>
        </div>

        <DCard style={{ padding: 12, marginBottom: 14, background: 'rgba(200,168,75,0.07)', borderColor: 'rgba(200,168,75,0.25)' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.gold, marginBottom: 6 }}>Cómo funciona</div>
          {[
            'Elige Manual o Análisis IA (cuesta Grooves; revisa campos en naranja si la confianza es baja).',
            'Sube hasta 3 fotos; la portada se comprueba con IA al guardar.',
            'Completa tipo (individual o conjunto), género, formato y estado. Opcional: sello, país, observaciones.',
            'Marca si es Joya o si aceptas ofertas por ese disco. Luego «Guardar en colección».',
          ].map((s, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 3, alignItems: 'flex-start' }}>
              <span style={{ color: C.gold, fontSize: 10, flexShrink: 0 }}>{i + 1}.</span>
              <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', lineHeight: 1.4 }}>{s}</span>
            </div>
          ))}
        </DCard>

        {!modo && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 12, textAlign: 'center' }}>¿Cómo quieres introducir los datos?</div>
            {iaGateReason === 'phone_not_verified' && user?.id ? (
              <DCard style={{ padding: 10, marginBottom: 10, background: 'rgba(239,159,39,0.08)', borderColor: 'rgba(239,159,39,0.28)' }}>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.88)', lineHeight: 1.45, marginBottom: 8 }}>{GROOVES_PHONE_GATE_USER_MESSAGE}</div>
                <BtnGold
                  type="button"
                  onClick={() => {
                    onClose?.();
                    onNavigate?.('ajustes', { initialSub: 'cuenta' });
                  }}
                  style={{ padding: '7px 12px', fontSize: 11, borderRadius: 8 }}>
                  Ir a Ajustes
                </BtnGold>
              </DCard>
            ) : null}
            <div style={{ display: 'flex', gap: 10 }}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => {
                  clearFormFields();
                  setModo('manual');
                  setDone(true);
                }}
                style={{
                  flex: 1,
                  padding: '16px 10px',
                  borderRadius: 12,
                  cursor: 'pointer',
                  textAlign: 'center',
                  border: '0.5px solid rgba(255,255,255,0.15)',
                  background: 'rgba(255,255,255,0.03)',
                }}
              >
                <div style={{ fontSize: 22, marginBottom: 4 }}>💿</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>Manual</div>
                <div style={{ fontSize: 10, color: '#5DCAA5', marginTop: 4 }}>Gratis</div>
              </div>
              <div
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (!puedeUsarIA) return;
                  clearFormFields();
                  setModo('ia');
                }}
                style={{
                  flex: 1,
                  padding: '16px 10px',
                  borderRadius: 12,
                  textAlign: 'center',
                  border: `0.5px solid ${puedeUsarIA ? 'rgba(200,168,75,0.4)' : 'rgba(255,255,255,0.08)'}`,
                  background: puedeUsarIA ? 'rgba(200,168,75,0.05)' : 'rgba(255,255,255,0.02)',
                  cursor: puedeUsarIA ? 'pointer' : 'not-allowed',
                  opacity: puedeUsarIA ? 1 : 0.55,
                }}
              >
                <div style={{ fontSize: 22, marginBottom: 4 }}>✨</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: puedeUsarIA ? C.gold : 'rgba(255,255,255,0.4)' }}>Análisis IA</div>
                <div style={{ fontSize: 10, marginTop: 4, color: puedeUsarIA ? C.gold : '#E24B4A' }}>
                  {groovesDisponibles == null
                    ? 'Cargando…'
                    : iaGateReason === 'phone_not_verified'
                      ? GROOVES_PHONE_GATE_USER_MESSAGE
                      : puedeUsarIA
                        ? `${AI_SCAN_COST} Grooves · Tienes ${groovesDisponibles} ★`
                        : `Necesitas ${AI_SCAN_COST} Grooves`}
                </div>
              </div>
            </div>
          </div>
        )}

        {modo === 'ia' && !done && !analyzing && (
          <div style={{ marginBottom: 12 }}>
            {iaGateReason === 'phone_not_verified' && user?.id ? (
              <DCard style={{ padding: 10, marginBottom: 8, background: 'rgba(239,159,39,0.08)', borderColor: 'rgba(239,159,39,0.28)' }}>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.88)', lineHeight: 1.45, marginBottom: 8 }}>{GROOVES_PHONE_GATE_USER_MESSAGE}</div>
                <BtnGold
                  type="button"
                  onClick={() => {
                    onClose?.();
                    onNavigate?.('ajustes', { initialSub: 'cuenta' });
                  }}
                  style={{ padding: '7px 12px', fontSize: 11, borderRadius: 8 }}>
                  Ir a Ajustes
                </BtnGold>
              </DCard>
            ) : null}
            <div style={{ fontSize: 11, color: C.gold, marginBottom: 8, textAlign: 'center' }}>
              {AI_SCAN_COST} Grooves solo si rellena título y artista · Tienes {groovesDisponibles ?? 0} ★ — sube la portada con texto legible
            </div>
            {fotoSlotsUi('ia')}
          </div>
        )}

        {modo === 'ia' && iaAnalysisError && !analyzing && (
          <DCard style={{ padding: 12, marginBottom: 12, background: 'rgba(226,75,74,0.08)', borderColor: 'rgba(226,75,74,0.35)' }}>
            <div style={{ fontSize: 12, color: '#E89797', lineHeight: 1.45, marginBottom: 8 }}>{iaAnalysisError}</div>
            <BtnGhost
              type="button"
              onClick={() => {
                setIaAnalysisError(null);
                fotoInputRef0.current?.click();
              }}
              style={{ padding: '6px 10px', fontSize: 11 }}
            >
              Reintentar (★ Portada)
            </BtnGhost>
          </DCard>
        )}

        {analyzing && (
          <DCard style={{ padding: 12, marginBottom: 12, background: 'rgba(83,74,183,0.1)', borderColor: 'rgba(83,74,183,0.3)' }}>
            <div style={{ fontSize: 12, color: C.purpleL }}>Analizando imagen…</div>
          </DCard>
        )}

        {done && modo === 'ia' && (
          <DCard style={{ padding: 10, marginBottom: 12, background: 'rgba(83,74,183,0.08)', borderColor: 'rgba(83,74,183,0.25)' }}>
            <div style={{ fontSize: 11, color: C.purpleL }}>
              {titulo || artista ? `Detectado: ${titulo}${artista ? ` — ${artista}` : ''}` : 'Disco identificado'}
            </div>
            {iaScanNote ? (
              <div style={{ fontSize: 10, color: C.warn, marginTop: 8, lineHeight: 1.45 }}>{iaScanNote}</div>
            ) : null}
          </DCard>
        )}

        {modo && done && (
          <>
            <TipoPublicacionRow esConjunto={esConjunto} onChangeES={setTipoConjunto} />
            {fotoSlotsUi('main')}

            {!esConjunto && (
              <>
                <FieldInput label="Artista *" value={artista} onChange={e => setArtista(e.target.value)} />
                <FieldInput label="Título *" value={titulo} onChange={e => setTitulo(e.target.value)} />
                <FieldInput label="Año" value={anio} onChange={e => setAnio(e.target.value.replace(/\D/g, '').slice(0, 4))} />
                <YesNoRow label="¿Es una edición especial?" value={esEdicionEspecial} onChange={setEdicionEspecialValue} />
                {esEdicionEspecial && (
                  <>
                    <FieldInput
                      label="Nombre de la edición *"
                      value={edicionNombre}
                      onChange={e => setEdicionNombre(e.target.value)}
                      placeholder="Ej: 40th Anniversary…"
                    />
                    <div style={{ width: '50%', marginBottom: 12, minWidth: 140 }}>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 5 }}>Número de catálogo</div>
                      <input
                        value={edicionCatalogo}
                        onChange={e => setEdicionCatalogo(e.target.value)}
                        style={{ ...inpBase, width: '100%' }}
                        onFocus={e => {
                          e.target.style.borderColor = C.gold;
                        }}
                        onBlur={e => {
                          e.target.style.borderColor = 'rgba(255,255,255,0.12)';
                        }}
                      />
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 5 }}>Qué incluye</div>
                      <input
                        value={edicionContenido}
                        onChange={e => setEdicionContenido(e.target.value)}
                        placeholder="Ej: 2 LPs + libro"
                        style={inpBase}
                        onFocus={e => {
                          e.target.style.borderColor = C.gold;
                        }}
                        onBlur={e => {
                          e.target.style.borderColor = 'rgba(255,255,255,0.12)';
                        }}
                      />
                    </div>
                    <YesNoRow
                      label="¿Es numerada?"
                      value={edicionNumerada}
                      onChange={v => {
                        setEdicionNumerada(v);
                        if (!v) setEdicionNumeroEjemplar('');
                      }}
                    />
                    {edicionNumerada && (
                      <FieldInput
                        label="Número de ejemplar"
                        value={edicionNumeroEjemplar}
                        onChange={e => setEdicionNumeroEjemplar(e.target.value)}
                        placeholder="Ej: 1247 / 5000"
                      />
                    )}
                  </>
                )}
              </>
            )}

            {esConjunto && (
              <>
                <FieldInput label="Título del conjunto *" value={conjuntoTitulo} onChange={e => setConjuntoTitulo(e.target.value)} />
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 5 }}>Número de discos *</div>
                  <input
                    type="number"
                    min={2}
                    value={conjuntoNumDiscos}
                    onChange={e => {
                      const n = parseInt(e.target.value, 10);
                      if (Number.isNaN(n)) setConjuntoNumDiscos(2);
                      else setConjuntoNumDiscos(Math.max(2, n));
                    }}
                    style={inpBase}
                    onFocus={e => {
                      e.target.style.borderColor = C.gold;
                    }}
                    onBlur={e => {
                      e.target.style.borderColor = 'rgba(255,255,255,0.12)';
                    }}
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 5 }}>Descripción del conjunto *</div>
                  <textarea
                    value={conjuntoDescripcion}
                    onChange={e => setConjuntoDescripcion(e.target.value)}
                    rows={4}
                    placeholder="Artistas, títulos, estado de cada disco…"
                    style={{ ...inpBase, minHeight: 90, resize: 'vertical' }}
                    onFocus={e => {
                      e.target.style.borderColor = C.gold;
                    }}
                    onBlur={e => {
                      e.target.style.borderColor = 'rgba(255,255,255,0.12)';
                    }}
                  />
                </div>
                <EstadoBloque value={estado} onChange={setEstado} notaConjunto />
              </>
            )}

            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 5 }}>Sello discográfico</div>
              <input
                value={sello}
                onChange={e => setSello(e.target.value)}
                style={{
                  ...inpBase,
                  border: `0.5px solid ${camposInciertos.some(c => String(c).toLowerCase() === 'sello') ? C.warn : 'rgba(255,255,255,0.12)'}`,
                }}
                onFocus={e => {
                  e.target.style.borderColor = C.gold;
                }}
                onBlur={e => {
                  e.target.style.borderColor = camposInciertos.some(c => String(c).toLowerCase() === 'sello')
                    ? C.warn
                    : 'rgba(255,255,255,0.12)';
                }}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 5 }}>País de edición</div>
              <input
                value={pais}
                onChange={e => setPais(e.target.value)}
                placeholder="ej: Reino Unido"
                style={{
                  ...inpBase,
                  border: `0.5px solid ${camposInciertos.some(c => String(c).toLowerCase() === 'pais') ? C.warn : 'rgba(255,255,255,0.12)'}`,
                }}
                onFocus={e => {
                  e.target.style.borderColor = C.gold;
                }}
                onBlur={e => {
                  e.target.style.borderColor = camposInciertos.some(c => String(c).toLowerCase() === 'pais')
                    ? C.warn
                    : 'rgba(255,255,255,0.12)';
                }}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 5 }}>Formato *</div>
              <select
                value={formato}
                onChange={e => setFormato(e.target.value)}
                style={inpBase}
                onFocus={e => {
                  e.target.style.borderColor = C.gold;
                }}
                onBlur={e => {
                  e.target.style.borderColor = 'rgba(255,255,255,0.12)';
                }}
              >
                {FORMATO_OPTS.map(f => (
                  <option key={f} value={f} style={{ background: '#1a1d28', color: '#fff' }}>
                    {f}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 5 }}>Género principal *</div>
              <select
                value={genero}
                onChange={e => {
                  setGenero(e.target.value);
                  setGeneroSub('');
                }}
                style={inpBase}
                onFocus={e => {
                  e.target.style.borderColor = C.gold;
                }}
                onBlur={e => {
                  e.target.style.borderColor = 'rgba(255,255,255,0.12)';
                }}
              >
                <option value="" style={{ background: '#1a1d28', color: '#fff' }}>
                  Selecciona categoría…
                </option>
                {GENEROS_PRINCIPALES.map(k => (
                  <option key={k} value={k} style={{ background: '#1a1d28', color: '#fff' }}>
                    {k}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 5 }}>Subgénero (opcional)</div>
              <input
                list="coleccion-subgeneros-datalist"
                value={generoSub}
                onChange={e => setGeneroSub(e.target.value)}
                placeholder={genero ? 'Escribe o elige…' : 'Primero elige género principal'}
                disabled={!genero}
                style={{ ...inpBase, opacity: genero ? 1 : 0.5 }}
                onFocus={e => {
                  e.target.style.borderColor = C.gold;
                }}
                onBlur={e => {
                  e.target.style.borderColor = 'rgba(255,255,255,0.12)';
                }}
              />
              <datalist id="coleccion-subgeneros-datalist">
                {(GENEROS_TREE[genero] || []).map(s => (
                  <option key={s} value={s} />
                ))}
              </datalist>
            </div>

            {!esConjunto && <EstadoBloque value={estado} onChange={setEstado} notaConjunto={false} />}

            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 5 }}>Observaciones</div>
              <textarea
                value={obs}
                onChange={e => setObs(e.target.value)}
                rows={3}
                placeholder="Estado real, qué incluye la edición, funda…"
                style={inpBase}
                onFocus={e => {
                  e.target.style.borderColor = C.gold;
                }}
                onBlur={e => {
                  e.target.style.borderColor = 'rgba(255,255,255,0.12)';
                }}
              />
            </div>
            <FieldInput
              label="Detalle conservación (opcional)"
              value={notasExtra}
              onChange={e => setNotasExtra(e.target.value)}
              placeholder="Ej: NM, funda original…"
            />

            <div
              style={{
                marginBottom: 14,
                paddingTop: 12,
                borderTop: '0.5px solid rgba(255,255,255,0.08)',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '14px 24px',
                alignItems: 'center',
              }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', margin: 0 }}>
                <Toggle checked={isJoya} onChange={e => setIsJoya(e.target.checked)} />
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>Marcar como Joya</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', margin: 0 }}>
                <Toggle checked={openToOffers} onChange={e => setOpenToOffers(e.target.checked)} />
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>Abierto a ofertas</span>
              </label>
            </div>

            {submitError ? (
              <DCard style={{ padding: 12, marginBottom: 12, borderColor: 'rgba(226,75,74,0.35)', background: 'rgba(226,75,74,0.08)' }}>
                <div style={{ fontSize: 12, color: '#E89797' }}>{submitError}</div>
              </DCard>
            ) : null}

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <BtnGhost onClick={onClose} style={{ padding: '10px 16px', fontSize: 12 }}>
                Cancelar
              </BtnGhost>
              <BtnGold
                onClick={handleGuardarColeccion}
                disabled={submitting}
                style={{ padding: '10px 20px', fontSize: 13, fontWeight: 700, opacity: submitting ? 0.75 : 1 }}>
                {submitting ? 'Guardando…' : 'Guardar en colección'}
              </BtnGold>
            </div>
          </>
        )}
      </div>
    </>
  );
}
